import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import organizationService from "~/modules/organization/services/organization.service";
import type { CreateRelationByUploadingIdCardParams } from "~/modules/relation/models/CreateRelationByUploadingIdCardParams.type";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";
import relationService from "~/modules/relation/service/relation.service";

export class AffiliationService extends RestService {
  protected AFFILIATION_LINKS = "affiliationLinks";
  protected CHILDREN = "children";
  protected PARENT = "parent";

  getBusinessUnitAffiliationChildren({
    businessUnitCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<RelationEntity>> {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getRelationsAffiliationChildrenPath()}`,
      {
        params,
      },
    );
  }

  getOrganizationAffiliationChildren({
    organizationCode,
    page,
    pageSize,
    searchParams,
  }: FetchPageServiceParameters): Promise<PaginatedList<RelationEntity>> {
    const params = {
      page,
      pageSize,
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsAffiliationChildrenPath()}`,
      {
        params,
      },
    );
  }

  createOrganizationAffiliationByUploadingIdCard({
    upload,
    organizationCode,
  }: CreateRelationByUploadingIdCardParams): Promise<void> {
    return this.$apiV2.post(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsAffiliationPath()}`,
      relationService.createUploadFormData(upload.file),
      relationService.createUploadConfig(upload.progress),
    );
  }

  createBusinessUnitAffiliationByUploadingIdCard({
    upload,
    businessUnitCode,
  }: CreateRelationByUploadingIdCardParams): Promise<void> {
    return this.$apiV2.post(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getRelationsAffiliationPath()}`,
      relationService.createUploadFormData(upload.file),
      relationService.createUploadConfig(upload.progress),
    );
  }

  deleteOrganizationAffiliation({
    organizationCode,
    targetOrganizationCode,
  }: GenericParameters & { targetOrganizationCode: number }): Promise<void> {
    return this.$apiV2.delete(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsAffiliationPath()}/${targetOrganizationCode}`,
    );
  }

  deleteBusinessUnitAffiliation({
    businessUnitCode,
    targetOrganizationCode,
  }: GenericParameters & { targetOrganizationCode: number }): Promise<void> {
    return this.$apiV2.delete(
      `${businessUnitService.getBusinessUnitPath(businessUnitCode)}/${this.getRelationsAffiliationPath()}/${targetOrganizationCode}`,
    );
  }

  getOrganizationAffiliationParent({
    organizationCode,
  }: GenericParameters): Promise<RelationEntity> {
    return this.$apiV2.get(
      `${organizationService.getOrganizationPath(organizationCode)}/${this.getRelationsAffiliationPath()}/${this.PARENT}`,
    );
  }

  getRelationsAffiliationPath(): string {
    return `${relationService.getRelationsPath()}/${this.AFFILIATION_LINKS}`;
  }

  getRelationsAffiliationChildrenPath(): string {
    return `${this.getRelationsAffiliationPath()}/${this.CHILDREN}`;
  }
}
export default new AffiliationService();
